import * as Api from '@ViewModels';
import { StyleDeclarationValue, css } from 'aphrodite';
import { observer } from 'mobx-react';
import * as React from 'react';
import { getDefaultDateStringValue } from '../../../../models/UiUtils';
import { Button } from '../../../../web/components-v2/Button';
import { Pill } from '../../../../web/components-v2/Pill';
import { getSlotMachineName } from '../../../models/slotMachines';
import { SlotMachineSymbolGroup } from '../../../components/svgs/slotMachines/SlotMachineSymbolGroup';
import { SlotMachineViewModel } from '../../../viewModels/slotMachines';
import { styleSheet } from './styles';

const symbolNames: Record<Api.SlotMachineSymbol, string> = {
	[Api.SlotMachineSymbol.Cherry]: 'Cherry',
	[Api.SlotMachineSymbol.LevitateCoin]: 'Levitate Coin',
	[Api.SlotMachineSymbol.QuestionMark]: 'Question Mark',
	[Api.SlotMachineSymbol.Seven]: 'Seven',
	[Api.SlotMachineSymbol.Tiger]: 'Tiger',
};

export interface ISpinRow {
	machineType: Api.SlotMachineType;
	onClick?: React.MouseEventHandler<HTMLButtonElement>;
	slotMachine?: SlotMachineViewModel;
}

const getLogoStyle = (machineType: Api.SlotMachineType) => {
	switch (machineType) {
		case Api.SlotMachineType.BoozeCruise: {
			return styleSheet.boozeCruiseLogo;
		}
		case Api.SlotMachineType.GoldRush: {
			return styleSheet.goldRushLogo;
		}
		default: {
			return styleSheet.highScoreLogo;
		}
	}
};

export const SpinRow: React.FC<ISpinRow> = observer(props => {
	const { machineType, onClick, slotMachine } = props;
	const machineName = getSlotMachineName(machineType);
	const logoStyle = getLogoStyle(machineType);
	const spinCount = slotMachine?.spinsRemaining?.length || 0;
	const disabled = spinCount < 1;
	return (
		<Button disabled={disabled} onClick={onClick} size='lg' styles={[styleSheet.spinRow]} variant='outline'>
			<span className={css(styleSheet.spinRowContent)}>
				<span aria-label={`${machineName} logo`} className={css(logoStyle)} role='img' />
				<span className={css(styleSheet.spinRowText)}>
					You have &nbsp;
					<span className={css(styleSheet.spinRowCount)}>{spinCount}</span>
					&nbsp;
					{`extra ${machineName} spin${spinCount > 1 || spinCount === 0 ? 's' : ''}.${
						spinCount > 0 ? ' Test your luck!' : ''
					}`}
				</span>
			</span>
		</Button>
	);
});

export interface IHistoryRow {
	className?: string;
	prizeLog: Api.IPrizeLog<Api.IGameToken<Api.ISlotMachineSpin>>;
	styles?: StyleDeclarationValue[];
}

export const HistoryRow: React.FC<IHistoryRow> = props => {
	const { prizeLog, className = '', styles = [] } = props;
	const { symbol, symbolCount } = prizeLog.gameToken.gameOutcome.winningPayline;
	return (
		<li className={`${className} ${css(styleSheet.historyRow, ...styles)}`}>
			<span className={css(styleSheet.historyRowPrize)}>{prizeLog.gameToken.gameOutcome.prize}</span>
			<span className={css(styleSheet.historyRowSymbols)}>
				<Pill
					color='blue'
					icon={<SlotMachineSymbolGroup symbols={[symbol]} symbolStyles={[styleSheet.historyRowSymbol]} />}
					label={`${symbolNames[symbol] ?? symbol} x ${symbolCount}`}
					variant='label'
				/>
			</span>
			<span className={css(styleSheet.historyRowFieldWithLabel)}>
				<span className={css(styleSheet.historyRowLabel)}>When:</span>
				<span>{getDefaultDateStringValue(prizeLog.gameToken.redemptionDate)}</span>
			</span>
			<span className={css(styleSheet.historyRowFieldWithLabel)}>
				<span className={css(styleSheet.historyRowLabel)}>For:</span>
				<span>{prizeLog.achievementDescription}</span>
			</span>
		</li>
	);
};
